import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './Marketplace.css';
import { isTestOrg } from '../../utils/testOrg';

const TIER_COLORS = {
  STARTUP: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  ENTERPRISE: { bg: '#faf5ff', text: '#6b21a8', border: '#c084fc' },
};

const INV_STATUS_COLORS = {
  PAID: { bg: '#d1fae5', text: '#065f46' },
  PENDING: { bg: '#fef3c7', text: '#92400e' },
  OVERDUE: { bg: '#fee2e2', text: '#991b1b' },
  VOID: { bg: '#f1f5f9', text: '#64748b' },
  CANCELLED: { bg: '#f1f5f9', text: '#64748b' },
};

function PlanCard({ plan, current, onSubscribe }) {
  const { t } = useTranslation();
  const tc = TIER_COLORS[plan.tier] || TIER_COLORS.STARTUP;
  const isCurrentPlan = current?.plan?.id === plan.id;
  return (
    <div className={`mp-plan-card ${isCurrentPlan ? 'mp-plan-current' : ''}`}
         style={{ borderTop: `4px solid ${tc.border}` }}>
      <div className="mp-plan-tier" style={{ background: tc.bg, color: tc.text }}>{plan.tier}</div>
      <h3 className="mp-plan-name">{plan.name}</h3>
      <div className="mp-plan-price">
        <span className="mp-plan-amount">${Number(plan.priceMonthly).toLocaleString()}</span>
        <span className="mp-plan-period">{t('BillingPage.perMonth')}</span>
      </div>
      {Number(plan.priceMonthlyFee) > 0 && (
        <div className="mp-plan-annual" style={{ color: '#64748b' }}>
          {t('BillingPage.plusProcessingFee', { amount: Number(plan.priceMonthlyFee).toFixed(2), defaultValue: `+ $${Number(plan.priceMonthlyFee).toFixed(2)} card processing fee` })}
        </div>
      )}
      {plan.priceAnnual && (
        <div className="mp-plan-annual">{t('BillingPage.annualSave', { amount: Number(plan.priceAnnual).toLocaleString(), percent: Math.round((1 - plan.priceAnnual / (plan.priceMonthly * 12)) * 100) })}</div>
      )}
      <ul className="mp-plan-features">
        {(Array.isArray(plan.features) ? plan.features : []).map((f, i) => (
          <li key={i}><i className="fas fa-check" style={{ color: tc.text, marginRight: 6 }} />{f.trim()}</li>
        ))}
      </ul>
      {isCurrentPlan ? (
        <button className="mp-btn-current" disabled>
          <i className="fas fa-check-circle" /> {t('BillingPage.currentPlan')}
        </button>
      ) : (
        <button className="mp-btn-primary mp-btn-full" onClick={() => onSubscribe(plan)}>
          {t('BillingPage.subscribe')}
        </button>
      )}
    </div>
  );
}

export default function BillingPage() {
  const { t } = useTranslation();
  const [plans, setPlans]               = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices]         = useState([]);
  const [tab, setTab]                   = useState('plans');
  const [loading, setLoading]           = useState(true);
  const [subscribing, setSubscribing]   = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [msg, setMsg]                   = useState('');
  const [cycle, setCycle]               = useState('ANNUAL');
  const [confirmPlan, setConfirmPlan]   = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get('/billing/plans'),
      axiosInstance.get('/billing/subscription'),
      axiosInstance.get('/billing/invoices'),
    ]).then(([pRes, sRes, iRes]) => {
      if (pRes.status === 'fulfilled') setPlans(pRes.value.data);
      if (sRes.status === 'fulfilled') setSubscription(sRes.value.data);
      if (iRes.status === 'fulfilled') setInvoices(iRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Returned from Stripe subscription checkout — confirm and clean the flag from the URL.
  useEffect(() => {
    if (window.location.hash.includes('subscribed=1')) {
      setMsg(t('BillingPage.subscribedToast', 'Subscription active — auto-renewal is on. Your payment method is saved with Stripe.'));
      window.history.replaceState(null, '', window.location.hash.replace(/[?&]subscribed=1/, ''));
      setTimeout(fetchAll, 1500); // webhook activates the sub a moment after redirect
    }
  }, [t, fetchAll]);

  const handleSubscribe = async (plan) => {
    setSubscribing(true); setMsg('');
    try {
      // New recurring model: subscribe returns a Stripe subscription-checkout URL — redirect straight
      // to it (card saved + auto-charge). No more "create a pending invoice then pay it" second step.
      const res = await axiosInstance.post('/billing/subscribe', { planId: plan.id, billingCycle: cycle });
      setConfirmPlan(null);
      if (res.data?.checkoutUrl) { window.location.href = res.data.checkoutUrl; return; }
      fetchAll();
      setMsg(t('BillingPage.subscribeSuccess', { name: plan.name }));
    } catch (err) {
      setMsg(err?.response?.data?.message || err?.response?.data || t('BillingPage.subscriptionFailed'));
    } finally { setSubscribing(false); }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axiosInstance.post('/billing/create-portal-session');
      if (res.data?.url && String(res.data.url).startsWith('http')) {
        window.location.href = res.data.url; return;
      }
      // Grandfathered / never subscribed via Stripe — guide them to enable auto-renewal.
      if (res.data?.needsSetup) {
        setTab('plans');
        setMsg(res.data.message || t('BillingPage.enableAutoRenewHint', 'Choose your plan to enable auto-renewal.'));
        return;
      }
      setMsg(res.data.message || t('BillingPage.redirectingPortal'));
    } catch { setMsg(t('BillingPage.portalUnavailable')); }
    finally { setPortalLoading(false); }
  };

  // Re-subscribe the current plan through Stripe to convert a grandfathered/manual subscription to
  // auto-renewal (card on file). Falls back to the plans tab if the plan isn't resolvable.
  const handleEnableAutoRenew = () => {
    if (subscription?.plan?.id) handleSubscribe(subscription.plan);
    else setTab('plans');
  };

  // Download an org-scoped HTML invoice (opens in a new tab; user can print / save as PDF).
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const res = await axiosInstance.get(`/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const w = window.open(url, '_blank');
      if (!w) { setMsg(t('BillingPage.popupBlocked', 'Please allow pop-ups to view the invoice.')); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setMsg(t('BillingPage.downloadFailed', 'Could not open the invoice. Please try again.'));
    }
  };

  // Pay an outstanding invoice via Stripe Checkout.
  const handlePay = async (invoiceId) => {
    try {
      const res = await axiosInstance.post(`/billing/invoices/${invoiceId}/checkout`);
      if (res.data?.checkoutUrl) { window.location.href = res.data.checkoutUrl; return; }
      setMsg(t('BillingPage.portalUnavailable'));
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.response?.data || t('BillingPage.portalUnavailable'));
    }
  };

  if (loading) return <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('BillingPage.loadingBilling')}</div>;

  const subStatus = subscription?.status || 'NONE';

  return (
    <div className="mp-wrap">
      {/* An exempt tenant is never charged, so say that before the plan cards pitch a price. The
          plans stay visible on purpose — QA still needs to exercise the subscribe/checkout flow. */}
      {isTestOrg() && (
        <div className="mp-testorg-note">
          <i className="fas fa-vial" />
          <span>
            <strong>{t('BillingPage.testOrgTitle', 'Test organization')}</strong>{' — '}
            {t('BillingPage.testOrgText',
               'Payment walls are bypassed for this tenant, so nothing below is enforced and this org will not be billed.')}
          </span>
        </div>
      )}
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-credit-card" /> {t('BillingPage.title')}</h2>
          {subscription && subscription.plan && (
            <p className="mp-subtitle">
              {t('BillingPage.currentLabel')} <strong>{subscription.plan.name}</strong> · {t('BillingPage.statusLabel')}&nbsp;
              <span style={{ color: subStatus === 'ACTIVE' ? '#065f46' : '#92400e' }}>{subStatus}</span>
              {subscription.renewalDate && ` · ${t('BillingPage.renews', { date: new Date(subscription.renewalDate).toLocaleDateString() })}`}
            </p>
          )}
          {subscription && subscription.plan && subStatus === 'ACTIVE' && subscription.autoRenew === false && (
            <div className="mp-subtitle" style={{ marginTop: 6 }}>
              <span style={{ color: '#92400e', marginRight: 10 }}>
                <i className="fas fa-exclamation-circle" /> {t('BillingPage.manualRenewalWarning', 'Auto-renewal is off — renewals are billed manually.')}
              </span>
              <button className="mp-btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={handleEnableAutoRenew}>
                <i className="fas fa-sync-alt" /> {t('BillingPage.enableAutoRenew', 'Enable auto-renewal')}
              </button>
            </div>
          )}
          {subscription && subscription.autoRenew && subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
            <p className="mp-subtitle" style={{ color: '#92400e', marginTop: 4, fontSize: 13 }}>
              <i className="fas fa-clock" /> {t('BillingPage.cancelsOn', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString(), defaultValue: `Cancels on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}` })}
            </p>
          )}
          {subscription && ((Array.isArray(subscription.addons) && subscription.addons.length > 0) || Number(subscription.processingFee) > 0) && (
            <div className="mp-subtitle" style={{ marginTop: 4, fontSize: 13 }}>
              <div>
                {subscription.plan?.name} — ${Number(subscription.plan?.priceMonthly || 0).toLocaleString()}/mo
              </div>
              {(subscription.addons || []).map((a, i) => (
                <div key={i} style={{ color: '#475569' }}>
                  + {a.frameworkName} {t('BillingPage.addonLabel', 'add-on')} — ${Number(a.monthlyPrice || 0).toLocaleString()}/mo
                </div>
              ))}
              {Number(subscription.processingFee) > 0 && (
                <div style={{ color: '#475569' }}>
                  + {t('BillingPage.processingFeeLabel', 'Payment processing fee')} — ${Number(subscription.processingFee).toFixed(2)}/mo
                </div>
              )}
              <div style={{ fontWeight: 700, marginTop: 2 }}>
                {t('BillingPage.monthlyTotal', 'Monthly total')}: ${Number(subscription.monthlyTotalWithFee ?? subscription.monthlyTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
              </div>
            </div>
          )}
        </div>
        <button className="mp-btn-secondary" onClick={handlePortal} disabled={portalLoading}>
          <i className={portalLoading ? 'fas fa-spinner fa-spin' : 'fas fa-external-link-alt'} /> {t('BillingPage.billingPortal')}
        </button>
      </div>

      {msg && <div className={`mp-msg ${msg.toLowerCase().includes('fail') ? 'mp-msg-err' : ''}`}>{msg}</div>}

      <div className="mp-tabs">
        {[['plans', 'fa-layer-group', t('BillingPage.tabPlans')],
          ['invoices', 'fa-file-invoice-dollar', t('BillingPage.tabInvoices', { count: invoices.length })],
        ].map(([key, icon, label]) => (
          <button key={key} className={`mp-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            <i className={`fas ${icon}`} /> {label}
          </button>
        ))}
      </div>

      {/* Plans tab */}
      {tab === 'plans' && (
        <div className="mp-section">
          <div className="mp-cycle-toggle">
            <span className="mp-cycle-label">{t('BillingPage.billingCycle')}</span>
            {['MONTHLY', 'ANNUAL'].map(c => (
              <button key={c} className={`mp-cycle-btn ${cycle === c ? 'active' : ''}`}
                      onClick={() => setCycle(c)}>
                {c === 'ANNUAL' ? t('BillingPage.cycleAnnual') : t('BillingPage.cycleMonthly')}
              </button>
            ))}
          </div>
          <div className="mp-plans-grid">
            {plans.map(plan => (
              <PlanCard key={plan.id} plan={plan} current={subscription}
                onSubscribe={(p) => setConfirmPlan(p)} />
            ))}
          </div>
          <div className="mp-pricing-note">
            <i className="fas fa-info-circle" /> {t('BillingPage.pricingNote')}
          </div>
        </div>
      )}

      {/* Invoices tab */}
      {tab === 'invoices' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('BillingPage.invoiceHistory')}</h3>
          {invoices.length === 0 ? (
            <div className="mp-empty">{t('BillingPage.noInvoices')}</div>
          ) : (
            <table className="mp-table">
              <thead>
                <tr><th>{t('BillingPage.colDescription')}</th><th>{t('BillingPage.colType')}</th><th>{t('BillingPage.colAmount')}</th><th>{t('BillingPage.colStatus')}</th><th>{t('BillingPage.colDue')}</th><th>{t('BillingPage.colPaid')}</th></tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const sc = INV_STATUS_COLORS[inv.status] || INV_STATUS_COLORS.PENDING;
                  return (
                    <tr key={inv.id}>
                      <td>{inv.description || '—'}</td>
                      <td>{inv.serviceType}</td>
                      <td style={{ fontWeight: 600 }}>
                        ${Number(inv.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        {Number(inv.processingFee) > 0 && (
                          <div style={{ fontWeight: 400, fontSize: 11, color: '#64748b' }}>
                            {t('BillingPage.inclProcessingFee', { amount: Number(inv.processingFee).toFixed(2), defaultValue: `incl. $${Number(inv.processingFee).toFixed(2)} processing fee` })}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: sc.bg, color: sc.text }}>
                          {inv.status}
                        </span>
                      </td>
                      <td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                          {inv.paidAt
                            ? <span>{new Date(inv.paidAt).toLocaleDateString()}</span>
                            : (inv.status === 'CANCELLED'
                                ? <span>—</span>
                                : <button className="mp-btn-primary" style={{ padding: '4px 14px', fontSize: 12 }} onClick={() => handlePay(inv.id)}>
                                    <i className="fas fa-credit-card" /> {t('BillingPage.pay', 'Pay')}
                                  </button>)}
                          <button className="mp-btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                                  title={t('BillingPage.downloadInvoice', 'Download invoice')}
                                  onClick={() => handleDownloadInvoice(inv.id)}>
                            <i className="fas fa-download" /> {t('BillingPage.invoice', 'Invoice')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Confirm subscribe modal */}
      {confirmPlan && (
        <div className="mp-overlay" onClick={() => setConfirmPlan(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3>{t('BillingPage.confirmSubscription')}</h3>
              <button onClick={() => setConfirmPlan(null)}><i className="fas fa-times" /></button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p>{t('BillingPage.subscribeToPrefix')} <strong>{confirmPlan.name}</strong> ({cycle})?</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f' }}>
                {cycle === 'ANNUAL'
                  ? t('BillingPage.pricePerYear', { amount: Number(confirmPlan.priceAnnual || confirmPlan.priceMonthly * 12).toLocaleString() })
                  : t('BillingPage.pricePerMonth', { amount: Number(confirmPlan.priceMonthly).toLocaleString() })}
              </p>
              {(() => {
                const fee = Number(cycle === 'ANNUAL' ? confirmPlan.priceAnnualFee : confirmPlan.priceMonthlyFee) || 0;
                if (fee <= 0) return null;
                const base = Number(cycle === 'ANNUAL' ? (confirmPlan.priceAnnual || confirmPlan.priceMonthly * 12) : confirmPlan.priceMonthly);
                const per = cycle === 'ANNUAL' ? 'year' : 'month';
                return (
                  <p style={{ fontSize: 14, color: '#334155' }}>
                    {t('BillingPage.plusProcessingFeeCycle', { amount: fee.toFixed(2), per, defaultValue: `+ $${fee.toFixed(2)} payment processing fee per ${per} (card processing costs, passed through at cost)` })}
                    <br />
                    <strong>{t('BillingPage.totalCharged', { amount: (base + fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), per, defaultValue: `Total charged: $${(base + fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per ${per}` })}</strong>
                  </p>
                );
              })()}
              <p style={{ color: '#64748b', fontSize: 13 }}>
                {t('BillingPage.recurringNote', { cycle: cycle === 'ANNUAL' ? 'year' : 'month', defaultValue: `You'll be taken to Stripe to enter your card. It's charged now and auto-renews each ${cycle === 'ANNUAL' ? 'year' : 'month'}. To change or cancel your plan, contact our support team.` })}
              </p>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setConfirmPlan(null)}>{t('BillingPage.cancel')}</button>
              <button className="mp-btn-primary" onClick={() => handleSubscribe(confirmPlan)} disabled={subscribing}>
                {subscribing ? t('BillingPage.processing') : t('BillingPage.confirmAndSubscribe')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
